/**
 * Daily rider earnings — one service date, per rider.
 *
 * What each rider has earned on the day's rides, split by stage: in transit
 * (assigned, not yet delivered) and delivered. It is a picture of EARNINGS:
 * whether the customer has paid and whether the rider has been paid out are
 * shown beside it, never mixed in. Every card, row, total and rider detail is
 * the same server answer under the same filters, so they cannot disagree.
 */

import { useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';
import type { DailyRiderEarningsCurrency, DailyRiderEarningsDelivery, DailyRiderEarningsRow } from '@cenox/shared';
import { Card, EmptyState, ErrorState, Input, Modal, Select, TableSkeleton, cn } from '../../components/ui';
import { GeoScopeFields } from '../../components/GeoScope';
import { useAuth } from '../../lib/auth';
import { money, todayIn } from '../../lib/format';
import { useAreaFilter, useLocationFilter, useLocationReady } from '../../lib/locationContext';
import { useDailyRiderEarnings, useDailyRiderEarningsDeliveries } from './data';

type Preset = 'today' | 'yesterday' | 'custom';

/** The calendar day before `iso` (YYYY-MM-DD), without leaving date arithmetic. */
function dayBefore(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

const IN_TRANSIT_HELP =
  'Rides a rider has accepted that are not yet delivered: accepted and awaiting pickup (Assigned), or picked up and ' +
  'on the way (In Transit). Pending rides that only name a rider are not counted until accepted. Cancelled, failed ' +
  'and returned rides are not included.';

const NOTE =
  'Rider Payouts starts from total earnings, which include delivered and in-transit rides. Cancelled rides are excluded. Delivery status and payment status are tracked separately. Cash a rider collects reduces their payout, never the earnings shown here.';

const SOURCE_LABEL: Record<DailyRiderEarningsDelivery['source'], string> = {
  shop: 'Shop',
  app: 'App',
  whatsapp: 'WhatsApp',
  not_recorded: 'Not recorded',
};

export function DailyRiderEarningsPage(): ReactNode {
  const { business } = useAuth();
  const timezone = business?.timezone ?? 'Africa/Lagos';
  const today = todayIn(timezone);
  const [preset, setPreset] = useState<Preset>('today');
  const [custom, setCustom] = useState(today);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<{ riderKey: string; riderName: string; currencyCode: string } | null>(null);

  const date = preset === 'today' ? today : preset === 'yesterday' ? dayBefore(today) : custom;
  // The sidebar's Country → State → Location, in both vocabularies the API reads.
  const area = useAreaFilter();
  const scope = useLocationFilter();
  const filters = { date, ...area, ...scope, ...(search.trim() ? { search: search.trim() } : {}) };
  const ready = useLocationReady();
  const report = useDailyRiderEarnings(filters, ready);
  const [payoutOf, setPayoutOf] = useState<{ row: DailyRiderEarningsRow; currencyCode: string } | null>(null);

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Daily rider earnings</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          What each rider earned on the day&apos;s rides, by service date in {timezone}. Earnings, not payouts.
        </p>
      </header>

      <Card>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <Select
            label="Date"
            value={preset}
            onChange={(e) => setPreset(e.target.value as Preset)}
            options={[
              { value: 'today', label: 'Today' },
              { value: 'yesterday', label: 'Yesterday' },
              { value: 'custom', label: 'Custom date' },
            ]}
          />
          {preset === 'custom' ? (
            <Input label="Service date" type="date" value={custom} max={today} onChange={(e) => setCustom(e.target.value || today)} />
          ) : null}
          <GeoScopeFields bare idPrefix="daily-earnings-scope" />
          <Input label="Rider" type="search" placeholder="Search riders" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </Card>

      {report.isError ? (
        <ErrorState message="Could not load rider earnings." onRetry={() => void report.refetch()} />
      ) : report.isPending ? (
        <TableSkeleton rows={4} columns={6} />
      ) : report.data.currencies.length === 0 ? (
        <EmptyState title="No rides on this date" description="Nothing assigned to a rider for the date and filters chosen." />
      ) : (
        report.data.currencies.map((c) => (
          <CurrencyReport
            key={c.currencyCode}
            report={c}
            onPayout={(row) => setPayoutOf({ row, currencyCode: c.currencyCode })}
            showCode={report.data.currencies.length > 1}
            onOpen={(riderKey, riderName) => setOpen({ riderKey, riderName, currencyCode: c.currencyCode })}
          />
        ))
      )}

      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="earnings-note">
        {NOTE}
      </p>

      <RiderDeliveries target={open} filters={filters} onClose={() => setOpen(null)} />
      {payoutOf ? <PayoutBreakdown target={payoutOf} filters={filters} onClose={() => setPayoutOf(null)} /> : null}
    </div>
  );
}

function Metric({
  label,
  value,
  help,
  strong,
}: {
  label: string;
  value: string;
  help?: string;
  strong?: boolean;
}): ReactNode {
  return (
    <div
      className={cn(
        'min-w-0 rounded-xl border bg-white p-3 shadow-sm sm:p-4',
        strong ? 'border-amber-300 ring-1 ring-amber-200' : 'border-slate-200',
      )}
    >
      <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-500">
        <span>{label}</span>
        {help ? (
          <span className="group relative inline-flex">
            <button type="button" aria-label={`About ${label}`} className="text-slate-400 hover:text-slate-600">
              <Info className="size-3.5" aria-hidden />
            </button>
            <span
              role="tooltip"
              className="pointer-events-none absolute left-1/2 top-5 z-10 hidden w-64 -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-xs normal-case tracking-normal text-white shadow-lg group-focus-within:block group-hover:block"
            >
              {help}
            </span>
          </span>
        ) : null}
      </div>
      <p className={cn('mt-1 overflow-x-auto whitespace-nowrap text-lg font-semibold leading-tight tabular-nums 2xl:text-xl', strong ? 'text-amber-700' : 'text-slate-900')}>{value}</p>
    </div>
  );
}

function CurrencyReport({
  report: c,
  showCode,
  onOpen,
  onPayout,
}: {
  report: DailyRiderEarningsCurrency;
  showCode: boolean;
  onOpen: (riderKey: string, riderName: string) => void;
  onPayout: (row: DailyRiderEarningsRow) => void;
}): ReactNode {
  const m = (v: string) => money(v, c.currencyCode);
  const t = c.totals;
  const ex = c.excluded;
  const recorded = (v: string) => (Number(v) !== 0 ? ` (rider payout recorded ${m(v)})` : '');
  const excluded = [
    ex.cancelledRides ? `${ex.cancelledRides} cancelled${recorded(ex.cancelledPayout)}` : null,
    ex.returnedRides ? `${ex.returnedRides} failed or returned${recorded(ex.returnedPayout)}` : null,
    ex.pendingAcceptanceRides
      ? `${ex.pendingAcceptanceRides} pending acceptance (rider named, not yet accepted; fees ${m(ex.pendingAcceptanceFees)})`
      : null,
    ex.unassignedRides ? `${ex.unassignedRides} with no rider yet` : null,
  ].filter(Boolean);

  return (
    <section className="space-y-4" aria-label={`Rider earnings in ${c.currencyCode}`}>
      {showCode ? <h2 className="text-sm font-semibold text-slate-700">{c.currencyCode}</h2> : null}

      {/*
        The order is fixed: rides, income, in transit, delivered, Rider Payouts.
        Rider total earnings is still calculated (Rider Payouts starts from it)
        and shown in the payout breakdown, not as a card or a column.
      */}
      <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5" data-testid="earnings-cards">
        <Metric label="Total rides" value={String(t.rides)} />
        <Metric label="Total income generated" value={m(t.income)} />
        <Metric label="In-transit earnings" value={m(t.inTransit)} help={IN_TRANSIT_HELP} />
        <Metric label="Delivered earnings" value={m(t.delivered)} />
        <PayoutCard totals={t} currencyCode={c.currencyCode} />
      </div>

      {/* Desktop: one table with its totals row. */}
      <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white md:block">
        <table className="w-full min-w-[52rem] text-sm" data-testid="earnings-table">
          <thead className="border-b border-amber-200 bg-amber-50/60 text-left text-xs uppercase tracking-wide text-slate-600">
            <tr>
              <th className="px-4 py-3">Rider</th>
              <th className="px-4 py-3 text-right">Total rides</th>
              <th className="px-4 py-3 text-right">Income generated</th>
              <th className="px-4 py-3 text-right">In-transit earnings</th>
              <th className="px-4 py-3 text-right">Delivered earnings</th>
              <th className="w-[13rem] min-w-[13rem] whitespace-nowrap px-4 py-3 text-right">Rider Payouts</th>
            </tr>
          </thead>
          <tbody>
            {c.riders.map((r) => (
              <tr key={r.riderKey} className="border-b border-slate-100 last:border-0 hover:bg-amber-50/40">
                <td className="px-4 py-2.5">
                  <button
                    type="button"
                    onClick={() => onOpen(r.riderKey, r.riderName)}
                    className="text-left font-medium text-slate-900 underline-offset-2 hover:underline"
                  >
                    {r.riderName}
                  </button>
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">{r.rides}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">{m(r.income)}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">{m(r.inTransit)}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">{m(r.delivered)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">
                  <PayoutCell r={r} currencyCode={c.currencyCode} onOpen={() => onPayout(r)} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-amber-300 bg-amber-50/60 font-semibold">
            <tr>
              <td className="px-4 py-3">Total</td>
              <td className="px-4 py-3 text-right tabular-nums">{t.rides}</td>
              <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">{m(t.income)}</td>
              <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">{m(t.inTransit)}</td>
              <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">{m(t.delivered)}</td>
              <td className="px-4 py-3 text-right tabular-nums" data-testid="payout-total">
                <PayoutTotal totals={t} currencyCode={c.currencyCode} />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Phone: a card per rider, the same metrics in the same order. */}
      <ul className="space-y-2 md:hidden" data-testid="earnings-rider-cards">
        {c.riders.map((r) => (
          <li key={r.riderKey}>
            <button
              type="button"
              onClick={() => onOpen(r.riderKey, r.riderName)}
              className="w-full rounded-xl border border-slate-200 bg-white p-3 text-left shadow-sm"
            >
              <p className="font-semibold text-slate-900">{r.riderName}</p>
              <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                <dt className="text-slate-500">Total rides</dt>
                <dd className="text-right tabular-nums">{r.rides}</dd>
                <dt className="text-slate-500">Income generated</dt>
                <dd className="whitespace-nowrap text-right tabular-nums">{m(r.income)}</dd>
                <dt className="text-slate-500">In-transit earnings</dt>
                <dd className="whitespace-nowrap text-right tabular-nums">{m(r.inTransit)}</dd>
                <dt className="text-slate-500">Delivered earnings</dt>
                <dd className="whitespace-nowrap text-right tabular-nums">{m(r.delivered)}</dd>
              </dl>
            </button>
            <div className="-mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-b-xl border border-t-0 border-slate-200 bg-violet-50/50 px-3 py-2 text-sm">
              <span className="font-medium text-slate-700">Rider Payouts</span>
              <PayoutCell r={r} currencyCode={c.currencyCode} onOpen={() => onPayout(r)} />
            </div>
          </li>
        ))}
        <li className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 text-sm font-semibold">
          <div className="flex justify-between gap-2">
            <span>Total — {t.rides} rides</span>
          </div>
          <div className="mt-1 flex justify-between gap-2 border-t border-amber-200 pt-1" data-testid="payout-total-mobile">
            <span>Rider Payouts</span>
            <PayoutTotal totals={t} currencyCode={c.currencyCode} />
          </div>
        </li>
      </ul>

      {/* The company's side of the same rides — reported, never booked here. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="commission">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">CenoHub commission — all rides</p>
          <p className="mt-1 text-lg font-semibold text-slate-900">{m(t.commissionAll)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">CenoHub commission — delivered</p>
          <p className="mt-1 text-lg font-semibold text-slate-900">{m(t.commissionDelivered)}</p>
        </div>
      </div>

      {excluded.length ? (
        <p className="text-xs text-slate-500" data-testid="earnings-excluded">
          Not included above: {excluded.join(' · ')}.
        </p>
      ) : null}
    </section>
  );
}

function RiderDeliveries({
  target,
  filters,
  onClose,
}: {
  target: { riderKey: string; riderName: string; currencyCode: string } | null;
  filters: { date: string } & Record<string, string | undefined>;
  onClose: () => void;
}): ReactNode {
  const list = useDailyRiderEarningsDeliveries(target ? { riderKey: target.riderKey, currencyCode: target.currencyCode } : null, filters);
  const m = (v: string, c: string) => money(v, c);
  return (
    <Modal open={target !== null} onClose={onClose} title={target ? `${target.riderName} — ${filters.date}` : ''} size="lg">
      {list.isError ? (
        <ErrorState message="Could not load this rider's deliveries." onRetry={() => void list.refetch()} />
      ) : list.isPending ? (
        <TableSkeleton rows={3} columns={6} />
      ) : list.data.data.length === 0 ? (
        <EmptyState title="No rides" description="Nothing counted for this rider under these filters." />
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="rider-deliveries">
          {list.data.data.map((d) => (
            <li key={d.id} className="py-3 text-sm">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="break-all font-mono text-xs font-medium text-slate-900">{d.orderRef ?? d.reference}</span>
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700">{SOURCE_LABEL[d.source]}</span>
                <span
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[11px] font-medium',
                    d.stage === 'delivered' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800',
                  )}
                >
                  {d.deliveryStatus ?? (d.stage === 'delivered' ? 'Delivered' : 'In transit')}
                </span>
                <span className="text-xs text-slate-500">Service date {d.serviceDate}</span>
              </div>
              <dl className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 sm:grid-cols-3">
                <div>
                  <dt className="text-xs text-slate-500">Delivery fee</dt>
                  <dd className="whitespace-nowrap tabular-nums">{m(d.fee, d.currencyCode)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500">CenoHub commission</dt>
                  <dd className="whitespace-nowrap tabular-nums">{m(d.commission, d.currencyCode)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500">Rider earnings</dt>
                  <dd className="font-medium tabular-nums">{m(d.riderEarnings, d.currencyCode)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500">Customer payment</dt>
                  <dd>{d.paymentStatus ?? 'Not recorded'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500">Rider payout</dt>
                  <dd>{d.riderPayoutStatus === 'paid_out' ? 'Paid out on a settlement statement' : 'Not paid out on a statement'}</dd>
                </div>
                <div className="col-span-2 sm:col-span-3">
                  <dt className="text-xs text-slate-500">Fee received by</dt>
                  <dd data-testid="fee-received-by" className={cn(d.cashBasis === 'unconfirmed' && 'font-medium text-amber-800')}>
                    {CUSTODY_LABEL[d.cashBasis]}
                  </dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

// ── Rider Payouts: the selected period's earnings less cash to rider ───────
//
// Straight from the earnings report's own figures (see dailyRiderEarnings.ts):
// Rider total earnings − delivery-fee cash a record says this rider received.
// Before any payment to the rider. A negative figure is ₦0 payable and "Rider
// owes company"; totals never net one rider's debt against another's payout.
// Cash nobody is recorded as receiving is never deducted, and never assumed.

export const PAYOUT_LABEL = "Selected period's earnings less cash collected; before any payment to the rider.";

function PayoutCell({ r, currencyCode, onOpen }: { r: DailyRiderEarningsRow; currencyCode: string; onOpen: () => void }): ReactNode {
  return (
    <button type="button" onClick={onOpen} className="min-w-0 text-right underline-offset-2 hover:underline" aria-label={`Rider Payouts breakdown for ${r.riderName}`}>
      <span className="block whitespace-nowrap font-semibold text-violet-800">{money(r.payout, currencyCode)}</span>
      {Number(r.owesCompany) > 0 ? (
        <span className="block text-xs font-medium text-red-700">
          Rider owes company <span className="whitespace-nowrap">{money(r.owesCompany, currencyCode)}</span>
        </span>
      ) : null}
      {r.unconfirmedCashRides > 0 ? (
        <span className="block text-xs font-medium text-amber-700">{r.unconfirmedCashRides} cash ride(s), receiver not recorded</span>
      ) : null}
    </button>
  );
}

function PayoutTotal({ totals, currencyCode }: { totals: DailyRiderEarningsCurrency['totals']; currencyCode: string }): ReactNode {
  return (
    <span className="block">
      <span className="block whitespace-nowrap text-violet-800">{money(totals.payout, currencyCode)}</span>
      {Number(totals.owesCompany) > 0 ? (
        <span className="block text-xs font-medium text-red-700">
          Riders owe company <span className="whitespace-nowrap">{money(totals.owesCompany, currencyCode)}</span>
        </span>
      ) : null}
      {totals.unconfirmedCashRides > 0 ? (
        <span className="block text-xs font-medium text-amber-700">{totals.unconfirmedCashRides} cash ride(s), receiver not recorded</span>
      ) : null}
    </span>
  );
}

function PayoutCard({ totals, currencyCode }: { totals: DailyRiderEarningsCurrency['totals']; currencyCode: string }): ReactNode {
  return (
    <div className="min-w-0 rounded-xl border border-violet-300 bg-white p-3 shadow-sm ring-1 ring-violet-200 sm:p-4" data-testid="payout-card">
      <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-500">
        <span>Rider Payouts</span>
      </div>
      <p className="mt-1 overflow-x-auto whitespace-nowrap text-lg font-semibold leading-tight tabular-nums text-violet-800 2xl:text-xl">
        {money(totals.payout, currencyCode)}
      </p>
      <div className="mt-1 space-y-0.5 text-xs">
        <p className="text-slate-500">{PAYOUT_LABEL}</p>
        {Number(totals.owesCompany) > 0 ? (
          <p className="font-medium text-red-700">
            Riders owe company <span className="whitespace-nowrap">{money(totals.owesCompany, currencyCode)}</span> ({totals.ridersOwing} rider{totals.ridersOwing === 1 ? '' : 's'}) — not deducted from the payouts above
          </p>
        ) : null}
        {totals.unconfirmedCashRides > 0 ? (
          <p className="font-medium text-amber-700">
            {totals.unconfirmedCashRides} cash ride(s) (<span className="whitespace-nowrap">{money(totals.unconfirmedCashFees, currencyCode)}</span>) with no record of who received the money — not deducted
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Who holds a delivery's fee, in words (apps/api finance/custody.ts). Shown, never acted on. */
const CUSTODY_LABEL: Record<DailyRiderEarningsDelivery['cashBasis'], string> = {
  rider_collection: 'the rider (recorded)',
  counter_receipt: 'the rider (Cash to Rider, on the counter’s receipt)',
  other_collector: 'someone other than this rider (recorded)',
  company_counter: 'the company (on the counter’s receipt)',
  company_payment: 'the company (paid by transfer, card or another non-cash method)',
  unconfirmed: 'not recorded — marked paid in cash; not deducted from anybody',
  unpaid: 'nobody yet — not paid',
};

const BASIS_LABEL: Record<DailyRiderEarningsDelivery['cashBasis'], string> = {
  rider_collection: 'Cash recorded as received by this rider',
  counter_receipt: 'Recorded at the counter as Cash to Rider',
  other_collector: 'Received by the office, company or someone else — not deducted',
  unconfirmed: 'Says cash, but nobody is recorded as receiving it — not deducted',
  company_counter: 'Received by the company on the counter’s receipt — not deducted',
  company_payment: 'Paid to the company by transfer, card or another non-cash method — not deducted',
  unpaid: 'Not paid yet — no cash to rider',
};

function PayoutBreakdown({
  target,
  filters,
  onClose,
}: {
  target: { row: DailyRiderEarningsRow; currencyCode: string };
  filters: { date: string } & Record<string, string | undefined>;
  onClose: () => void;
}): ReactNode {
  const r = target.row;
  const m = (v: string) => money(v, target.currencyCode);
  const list = useDailyRiderEarningsDeliveries({ riderKey: r.riderKey, currencyCode: target.currencyCode }, filters);
  const rides = list.data?.data ?? [];
  return (
    <Modal open onClose={onClose} title={`Rider Payouts — ${r.riderName}`} size="lg">
      <div className="space-y-3 text-sm" data-testid="payout-breakdown">
        <p className="rounded-md bg-violet-50 px-3 py-2 text-xs text-violet-900">
          {PAYOUT_LABEL} Service date {filters.date}.
        </p>
        <dl className="divide-y divide-slate-100">
          <div className="flex justify-between gap-3 py-1.5">
            <dt className="text-slate-600">Delivered earnings (ready)</dt>
            <dd className="whitespace-nowrap tabular-nums">{m(r.delivered)}</dd>
          </div>
          <div className="flex justify-between gap-3 py-1.5">
            <dt className="text-slate-600">In-transit earnings (pending — not ready for payment)</dt>
            <dd className="whitespace-nowrap tabular-nums">{m(r.inTransit)}</dd>
          </div>
          <div className="flex justify-between gap-3 py-1.5 font-medium">
            <dt>Rider total earnings</dt>
            <dd className="whitespace-nowrap tabular-nums">{m(r.total)}</dd>
          </div>
          <div className="flex justify-between gap-3 py-1.5">
            <dt className="text-slate-600">Cash to rider (recorded)</dt>
            <dd className="whitespace-nowrap tabular-nums">−{m(r.cashToRider)}</dd>
          </div>
          <div className="flex justify-between gap-3 py-2 font-semibold">
            <dt>Rider Payouts</dt>
            <dd className="whitespace-nowrap tabular-nums text-violet-800">{m(r.payout)}</dd>
          </div>
          {Number(r.owesCompany) > 0 ? (
            <div className="flex justify-between gap-3 py-1.5 font-medium text-red-700">
              <dt>Rider owes company</dt>
              <dd className="whitespace-nowrap tabular-nums">{m(r.owesCompany)}</dd>
            </div>
          ) : null}
        </dl>
        {Number(r.inTransit) > 0 ? (
          <p className="text-xs text-amber-800">
            {m(r.inTransit)} of this is for rides still in transit: pending until delivered, not ready for payment.
          </p>
        ) : null}
        {r.unconfirmedCashRides > 0 ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {r.unconfirmedCashRides} ride(s) ({m(r.unconfirmedCashFees)}) say the customer paid cash, but nothing records who received
            it. They are not deducted and not assumed — record who received the money on each ride to settle it.
          </p>
        ) : null}
        <h3 className="pt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Cash deductions by ride</h3>
        {list.isError ? (
          <ErrorState message="Could not load this rider's rides." onRetry={() => void list.refetch()} />
        ) : list.isPending ? (
          <TableSkeleton rows={2} columns={3} />
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="payout-rides">
            {rides.map((d) => (
              <li key={d.id} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5">
                <span className="min-w-0">
                  <span className="break-all font-mono text-xs">{d.orderRef ?? d.reference}</span>{' '}
                  <span className="text-xs text-slate-500">{d.stage === 'in_transit' ? 'in transit (pending)' : 'delivered'} · {BASIS_LABEL[d.cashBasis]}</span>
                </span>
                <span className="tabular-nums">
                  {m(d.riderEarnings)} {Number(d.cashToRider) > 0 ? <span className="text-red-700">−{m(d.cashToRider)}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
